import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SectionLabel from '../components/SectionLabel';
import AnimatedSection from '../components/AnimatedSection';
import logoSlogan from '../assets/Guebly_with_slogan.png';

const Section = styled.section`
  padding: 140px 80px;

  @media (max-width: 900px) {
    padding: 80px 24px;
  }
`;

const Inner = styled.div`
  max-width: 1360px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 90px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const Left = styled.div``;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(52px, 8vw, 108px);
  line-height: 0.88;
  letter-spacing: 0.02em;
  margin-bottom: 44px;

  .accent {
    background: ${({ theme }) => theme.gradients.brand};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Body = styled.p`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 17px;
  font-weight: 400;
  line-height: 1.82;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 22px;

  strong {
    color: rgba(255,255,255,0.9);
    font-weight: 600;
  }
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const Card = styled.div`
  background: linear-gradient(135deg, rgba(0,163,255,0.05) 0%, rgba(107,0,255,0.05) 100%);
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 48px 42px;
  @media (max-width: 600px) { padding: 32px 24px; }
  position: relative;
  overflow: hidden;
  transition: border-color 0.35s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderHover};
  }

  &::after {
    content: '';
    position: absolute;
    top: 0; left: 0;
    width: 3px; height: 100%;
    background: ${({ theme }) => theme.gradients.brand};
  }
`;

const Quote = styled.blockquote`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 30px;
  line-height: 1.22;
  letter-spacing: 0.05em;
  color: #fff;
  margin-bottom: 26px;

  &::before {
    content: '"';
    font-size: 80px;
    line-height: 0;
    vertical-align: -36px;
    margin-right: 4px;
    background: ${({ theme }) => theme.gradients.brand};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const QuoteBody = styled.p`
  font-size: 15px;
  line-height: 1.78;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LogoBox = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  @media (max-width: 480px) { padding: 20px 18px; }
  background: rgba(0,0,0,0.3);
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const LogoImg = styled.img`
  width: 240px;
  max-width: 100%;
  object-fit: contain;
`;

export default function Manifesto() {
  const { t } = useTranslation();

  return (
    <Section id="manifesto">
      <Inner>
        <AnimatedSection direction="left">
          <Left>
            <SectionLabel>{t('manifesto.label')}</SectionLabel>
            <Title>
              {t('manifesto.titleLine1')}<br />
              {t('manifesto.titleLine2')}<br />
              <span className="accent">{t('manifesto.titleAccent')}</span>
            </Title>
            <Body dangerouslySetInnerHTML={{ __html: t('manifesto.body1_html') }} />
            <Body dangerouslySetInnerHTML={{ __html: t('manifesto.body2_html') }} />
            <Body dangerouslySetInnerHTML={{ __html: t('manifesto.body3_html') }} />
          </Left>
        </AnimatedSection>

        <AnimatedSection direction="right">
          <Right>
            <Card>
              <Quote>
                "{t('manifesto.quote')}"
              </Quote>
              <QuoteBody>
                {t('manifesto.quoteBody')}
              </QuoteBody>
            </Card>

            <LogoBox>
              <LogoImg src={logoSlogan} alt="Guebly Games — Criando Universos, Conectando Pessoas" />
            </LogoBox>
          </Right>
        </AnimatedSection>
      </Inner>
    </Section>
  );
}
